import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon"; // Poolcorp Custom Code
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Poolcorp Custom Code */
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import {
    focusStyles,
    popoverStyles,
    sideMenuLinkActiveStyles,
    sideMenuLinkStyles,
    sideMenuWrapperStyles,
} from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import { languageCodeWithoutLocal, languageDescriptionWithoutLocal } from "@store/utilities/formatLanguages";
/* Poolcorp Custom Code */

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    // Poolcorp Custom Code
    popover?: PopoverPresentationProps;
    languageItem?: TypographyProps;
    currentLanguage?: LinkPresentationProps;
    currentLanguageIcon?: IconProps;
    languageItemClickable?: ClickableProps;
    selectedLanguageItemClickable?: ClickableProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;
            background-color: transparent;
            cursor: pointer;
        `,
    },
    languageIcon: {
        size: 22,
        src: Globe,
        css: css`
            margin-right: 5px;
        `,
    },
    languageSelect: {
        iconProps: {
            color: "common.background",
        },
        cssOverrides: {
            formInputWrapper: css`
                width: 100px;
            `,
            inputSelect: css`
                border: none;
                text-transform: uppercase;
                font-size: ${TYPOGRAPHY.size.smaller};
                background-color: transparent;
            `,
        },
    },
    popover: {
        wrapperProps: {
            css: css`
                height: 100%;
                display: flex;
                align-items: center;
            `,
        },
        contentBodyProps: {
            css: css`
                ${popoverStyles()}
                ${sideMenuWrapperStyles("0.5em 0")}
                max-height: none;
                color: inherit;
            `,
        },
    },
    languageItem: {
        color: "text.main",
    },
    languageItemClickable: {
        css: css`
            ${sideMenuLinkStyles("0.5em 1em")}
        `,
    },
    selectedLanguageItemClickable: {
        css: css`
            ${sideMenuLinkStyles("0.5em 1em")}
            ${sideMenuLinkActiveStyles()}
        `,
    },
    currentLanguage: {
        typographyProps: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
        css: css`
            ${focusStyles()}
            text-transform: uppercase;
            height: 100%;
        `,
    },
    currentLanguageIcon: {
        src: ChevronDown,
        width: 14,
        height: 14,
        css: css`
            margin-left: 0.25em;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    const element = React.useRef<HTMLUListElement>(null); // Poolcorp Custom Code
    const popover = React.useRef<HTMLUListElement>(null); // Poolcorp Custom Code

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    /* Poolcorp Custom Code */
    const closePopover = () => {
        popover.current && (popover.current! as any).closePopover();
    };

    const languageLink = (
        <Link {...styles.currentLanguage}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage src={currentLanguage.imageFilePath} alt="" />
                ) : (
                    <Icon {...styles.languageIcon} />
                ))}
            {currentLanguage ? languageCodeWithoutLocal(currentLanguage.languageCode) : "--"}
            <Icon {...styles.currentLanguageIcon} />
        </Link>
    );

    return (
        <StyledWrapper {...styles.languageWrapper}>
            <Popover
                {...styles.popover}
                contentBodyProps={{
                    ...styles.popover?.contentBodyProps,
                    as: "div",
                    ref: element,
                    css: css`
                        ${styles.popover?.contentBodyProps?.css}
                    `,
                }}
                positionFunction={() => {
                    return {
                        position: "absolute",
                        right: 0,
                        top: "calc(100% - 10px)",
                    };
                }}
                ref={popover}
                insideRefs={[element]}
                popoverTrigger={languageLink}
            >
                {languages.map(c => (
                    <Clickable
                        key={c.id}
                        {...(c.id === currentLanguage?.id
                            ? styles.selectedLanguageItemClickable
                            : styles.languageItemClickable)}
                        onClick={() => setLanguage({ languageId: c.id })}
                    >
                        <Typography {...styles.languageItem}>
                            {languageDescriptionWithoutLocal(c.description)}
                        </Typography>
                    </Clickable>
                ))}
            </Popover>
        </StyledWrapper>
    );
    /* Poolcorp Custom Code */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
